/**
 * L'état du stock valorisé, article par article — et à une date passée si on le demande.
 *
 * *La route existe depuis la 066 et n'avait jamais eu d'écran.* Elle en gagne un avec la demande
 * de Chris du 15/09/2026 — « on doit accéder à toutes les fonctions par le menu Stock » — parce
 * qu'elle répond à la question que l'inventaire pose : combien l'application croit-elle avoir, et
 * combien cela vaut-il.
 *
 * ## Deux filtres, et ils ne font pas la même chose
 *
 * **La date** coupe la suite des mouvements : c'est l'interrogation « où en était le stock ce
 * jour-là ». Elle borne le total ET les lignes du même coup — sans quoi l'écran montrerait un
 * total à une date et des lignes à une autre, et personne ne saurait laquelle croire.
 *
 * **Le magasin** ne coupe rien du tout : il choisit ce qu'on regarde d'un calcul fait sur tous les
 * mouvements. C'est la conséquence de la décision de Chris du 15/09/2026 — un seul coût moyen par
 * article, quel que soit le magasin —, et c'est ce qui rend les valeurs des magasins additives.
 *
 * ## Ce qui n'y figure pas
 *
 * Les 2 410 articles du référentiel qui n'ont jamais bougé. Les lister à zéro ferait chercher la
 * ligne utile dans du vide.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { chargerEtatDuStock } from "./api";
import type { EtatDuStockParArticle } from "./api";

/** Un décimal sans les zéros inutiles : « 12.000 » se lit « 12 ». */
function sansZeros(valeur: string): string {
  return valeur.includes(".") ? valeur.replace(/\.?0+$/, "") : valeur;
}

export function EtatDuStock(): JSX.Element {
  const [vue, setVue] = useState<EtatDuStockParArticle | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [date, setDate] = useState("");
  const [magasin, setMagasin] = useState("");

  const recharger = useCallback((): void => {
    chargerEtatDuStock(date === "" ? undefined : date, magasin === "" ? undefined : magasin)
      .then((r) => {
        setVue(r);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, [date, magasin]);

  useEffect(() => {
    recharger();
  }, [recharger]);

  /*
    Le total, calculé ici et pas demandé au serveur.

    Il n'additionne QUE ce qui est affiché : un total venu d'ailleurs ne correspondrait pas aux
    lignes sous les yeux dès qu'un filtre est posé, et c'est le genre d'écart qu'on met une heure
    à comprendre.
  */
  const total = (vue?.articles ?? []).reduce((t, a) => t + Number(a.etat.valeur), 0);

  return (
    <section className="etat-stock">
      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      <div className="etat-stock__filtres">
        <label>
          À la date du
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
            }}
          />
          <small>Vide : l&apos;état d&apos;aujourd&apos;hui.</small>
        </label>

        {/* Masqué quand il n'y a qu'un magasin : il n'y a rien à filtrer. */}
        {(vue?.sites.length ?? 0) > 1 && (
          <label>
            Magasin
            <select
              value={magasin}
              onChange={(e) => {
                setMagasin(e.target.value);
              }}
            >
              <option value="">Tous les magasins</option>
              {(vue?.sites ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.denomination}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {vue === null ? (
        <p className="etat">Lecture du stock…</p>
      ) : vue.articles.length === 0 ? (
        <p className="etat">
          {magasin === ""
            ? "Aucun article n'a encore bougé en stock."
            : "Aucun article n'a bougé dans ce magasin."}
        </p>
      ) : (
        <>
          <p className="etat-stock__total">
            {vue.articles.length} référence{vue.articles.length > 1 ? "s" : ""} ·{" "}
            <strong>{total.toFixed(2)} €</strong>
          </p>
          <table className="etat-stock__lignes">
            <thead>
              <tr>
                <th>Code</th>
                <th>Libellé</th>
                <th>Quantité</th>
                <th>Coût moyen</th>
                <th>Valeur</th>
              </tr>
            </thead>
            <tbody>
              {vue.articles.map((a) => (
                <tr key={a.id} className={a.etat.negatif ? "alerte" : undefined}>
                  <td className="etat-stock__code">{a.code}</td>
                  <td>{a.description}</td>
                  <td>
                    {sansZeros(a.etat.quantite)} {a.unite ?? ""}
                  </td>
                  {/* Le coût moyen est celui de l'ARTICLE, pas du magasin : il n'y en a qu'un
                      (décision de Chris du 15/09/2026). Il ne change donc pas quand on filtre. */}
                  <td>{sansZeros(a.etat.cmup)} €</td>
                  <td>{a.etat.valeur} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
